import { useContext, useMemo, useState } from "react";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { View } from "@common/types/views/views";
import { isHistoryDetailsResponse } from "@common/types/auditLog";
import { historyDrawerContext } from "../../historyDrawerContext";
import { useHistoryDetailsListParams } from "../useHistoryDetailsListParams";
import { getActivityDescription } from "../../utils/getActivityDescription";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import styles from "../HistoryDetails.module.scss";
import splitDrawerStyles from "../split/SplitHistoryDrawer.module.scss";
import {
    buildScheduleActivityTable,
    ScheduleActivityTable as ScheduleActivityTableData,
} from "./scheduleActivityRows";
import {
    createDefaultScheduleActivityDetailsView,
    createScheduleActivityDetailsViewConfig,
    ScheduleActivityDetailsView,
} from "./scheduleActivityDetailsViewConfig";

const DETAILS_LIMIT = 100;

interface ScheduleActivityTableProps {
    table: ScheduleActivityTableData;
    defaultView: ScheduleActivityDetailsView;
}

const ScheduleActivityTable = ({
    table,
    defaultView,
}: ScheduleActivityTableProps) => {
    const [view, setView] = useState<View>(defaultView);
    const viewConfig = useMemo(
        () => createScheduleActivityDetailsViewConfig(table.changedFieldLabel),
        [table.changedFieldLabel],
    );
    return (
        <ViewTable
            data={table.rows}
            view={view}
            config={viewConfig}
            onViewChange={setView}
            emptyView={() => <EmptyState header="No changes found" />}
            className={styles.historyDetailsView}
        />
    );
};

export const ScheduleActivityHistoryDrawer = () => {
    const { open, historyRow, closeDrawer } = useContext(historyDrawerContext);
    const [isOpen, setIsOpen] = useState(false);
    const shouldShowContent = isOpen || open;
    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const { data, isLoading, isError } = useGetHistoryDetailsQuery({
        ...listParams,
        date: historyRow?.date ?? fallbackDate,
        sourceType: historyRow?.sourceType ?? "",
        resourceType: historyRow?.resourceType,
        actorId: historyRow?.actorId,
        organizationActorId: historyRow?.historyItem.organizationActorId,
        entityIds: historyRow?.entityIds,
        changeTypes: historyRow?.changeTypes,
        sourceIds: historyRow?.historyItem.sourceIds,
        page: 1,
        limit: DETAILS_LIMIT,
        options: { enabled: shouldShowContent && !!historyRow },
    });

    const table = useMemo(
        () =>
            buildScheduleActivityTable({
                sourceType: historyRow?.sourceType ?? "",
                details:
                    data && isHistoryDetailsResponse(data) ? data.data : [],
            }),
        [data, historyRow?.sourceType],
    );

    const headerAddonLeft = historyRow ? (
        <ActivityDrawerHeader
            historyRow={historyRow}
            activityTitle={getActivityDescription(historyRow)}
        />
    ) : null;

    const renderContent = () => {
        if (isLoading || !historyRow) {
            return <Loader />;
        }
        if (isError) {
            return (
                <div className={splitDrawerStyles.emptyContainer}>
                    <EmptyState header="Failed to load changes" />
                </div>
            );
        }
        if (table.rows.length === 0) {
            return (
                <div className={splitDrawerStyles.emptyContainer}>
                    <EmptyState header="No changes found" />
                </div>
            );
        }
        return (
            <ScheduleActivityTable
                key={`${table.kind}-${historyRow.id}`}
                table={table}
                defaultView={createDefaultScheduleActivityDetailsView({
                    kind: table.kind,
                    resourceType: historyRow.resourceType,
                })}
            />
        );
    };

    return (
        <Drawer
            open={open}
            onClose={closeDrawer}
            direction="right"
            onAnimationEnd={setIsOpen}
            headerAddonLeft={headerAddonLeft}
            size="half"
            noPadding
        >
            {shouldShowContent && (
                <div className={styles.drawerContent}>{renderContent()}</div>
            )}
        </Drawer>
    );
};
